import {Component, ElementRef, EventEmitter, Injector, Input, Output, SimpleChanges, ViewChild, ViewEncapsulation, ChangeDetectorRef} from '@angular/core';
import {GeneralChartComponent} from "../general-chart/general-chart.component";
import {MatTableDataSource} from "@angular/material/table";
import {MatSort, MatSortable} from "@angular/material/sort";
import {PortfolioService} from "../../pages/portfolio/portfolio.service";
import {NavigationStateService} from "../../shared/services/navigation-state.service";
import { HighchartsChartComponent } from "highcharts-angular";
import { MaterialModule } from "src/app/app.materials";
import { InfoComponent } from "src/app/components/info/info.component";
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AreaChartComponent } from "../area-chart/area-chart.component";
import { DonutChartComponent } from "../donut-chart/donut-chart.component";
import * as Highcharts from "highcharts/highstock";
import { ExportService } from 'src/app/shared/services/export.service';
import { ExportControlComponent } from 'src/app/components/export-control/export-control.component';
import { NextBestTradeComponent } from 'src/app/components/next-best-trade/next-best-trade.component';


@Component({
  selector: 'app-efficient-frontier-chart',
  templateUrl: './efficient-frontier-chart.component.html',
  styleUrls: ['./efficient-frontier-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [HighchartsChartComponent, MaterialModule, InfoComponent, FormsModule, CommonModule, AreaChartComponent, DonutChartComponent, ExportControlComponent, NextBestTradeComponent]
})
export class EfficientFrontierChartComponent extends GeneralChartComponent {
  @Input() optimWeights: any[] = [];
  @Input() isPrint = false;
  @Input() optim: string;
  @Input() optimizationData: any = null;
  @Input() efficientChartMaxSharpeData: any = null;
  @Input() resultsTableData: any[] = [];
  @Input() minVolatility: any;
  @Input() maxVolatility: any;
  @Input() volatilityLine: any;

  @Output() pfOptim: EventEmitter<any> = new EventEmitter<any>();
  @Output() calculateClicked = new EventEmitter<any>();
  @Output() sliderChanged = new EventEmitter<any>();
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  @ViewChild('slider', { read: ElementRef }) slider: ElementRef;

  sliderLocked = true;

  showStocks = true;
  showBenchmark = false;
  showUnconstrainted = false;
  showEfWeights = false;
  service: PortfolioService;
  navigationStateService: NavigationStateService;
  dataSource = new MatTableDataSource<any>();
  columns: any[] = ['company_name', 'weight'];

  companyData: any[] = []; // store company return, volatility, and sharpe ratio indexed by ticker

  sliderVal = 0;
  sliderMax = 100;
  sliderData: any = [];
  sliderPortfolioData: any = {};
  sliderVolatility: any = 0;
  sliderLoaded = false;

  // Fixed point properties
  fixedPointTangencyPoint: number | null = null;
  fixedMaxSharpePoint: number | null = null;

  customPointAdded = false;
  currentIndex = 0;
  currentReturn: any = {}

  areaChartData: any[] = [];
  areaChartColors: string[] = [];
  cardExporting: Record<string, boolean> = {};
  exportService: ExportService;
  constructor(injector: Injector, private cdr: ChangeDetectorRef) {
    super(injector);
    this.chartConstructor = 'chart';
    this.service = injector.get(PortfolioService);
    this.navigationStateService = injector.get(NavigationStateService);
    this.exportService = injector.get(ExportService);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.optimWeights) {
      this.optimWeights = changes.optimWeights.currentValue;
      this.initAreaChart();
    }
    if (changes.optim) {
      this.optim = changes.optim.currentValue;
      this.initSlider();
    }
    if (changes.optimizationData) {
      this.optimizationData = changes.optimizationData.currentValue;
      if (!this.optim) {
        this.optim = this.optimizationData.optim_parameters.optim_performance;
      }
      this.initSlider();
    }
    if (changes.series) {
      this.series = changes.series.currentValue;
      this.initChart();
    }
    if (changes.efficientChartMaxSharpeData) {
      this.initSlider();
    }
    if (changes.resultsTableData) {
      this.resultsTableData = changes.resultsTableData.currentValue;
      this.initCompanyData();
    }
  }

  initAreaChart() {
    this.areaChartData = [];
    this.areaChartColors = [];
    const baseColor = '#424CCD';
        
    const zip = (a, b) => a.map((k, i) => [k, b[i]]);
    let i = 0;
    let chartData: any[] = [];
    this.optimWeights.map((d: any) => {
        this.areaChartColors.push(
            Highcharts.color(baseColor).brighten((0.5 / this.optimWeights.length) * i).get('rgb') as string
        );
        chartData.push({
            name: d.company_name,
            data: zip(d.vol, d.id_w)
        });
        i++;
    });
    this.areaChartData = chartData;
  }

  initCompanyData() {
    this.companyData = this.resultsTableData.reduce((acc, d) => {
      acc[d.Ticker] = {
        historicalReturn: d.historicalReturn,
        expectedReturn: d.expectedReturn,
        volatility: d.volatility,
        sharpeRatio: d.sharpe
      };
      return acc;
    }, {});
    this.initChart();
  }

  getConstraintForAsset(asset: string) {
    let result = null;
    this.optimizationData.group_constraints.forEach(function (constraint) {
      if (constraint.type.id === 'asset') {
        if (constraint.items.some(item => item.name === asset)) {
          result = {
            min: constraint.min / 100,
            max: constraint.max / 100
          }
        }
      }
    })

    return result;
  }

  onFixedPointClick(index: number) {
    if (this.sliderLocked) {
      return;
    }
    this.sliderVal = index;
    this.addPointToChart();
  }


  onCompanyClick(ticker: string) {
    this.service.openCompanyInfoDialog(ticker)
  }

  addPointToChart() {
    const index = this.sliderVal;
    if (this.companyData && Object.keys(this.companyData).length && this.optimWeights && this.optimWeights.length) {

      // update table data
      this.sliderData = [];
      this.optimWeights.map(o => {
        const companyName = o.company_name;
        const constraint = this.getConstraintForAsset(companyName);
        this.sliderData.push({
          ticker: o.Ticker,
          companyName: companyName,
          minWeight: constraint ? constraint.min * 100 : 0,
          maxWeight: constraint ? constraint.max * 100 : 0,
          allocation: o.id_w[index],
          expectedReturn: this.companyData[o.Ticker].expectedReturn,
          historicalReturn: this.companyData[o.Ticker].historicalReturn,
          sharpeRatio: this.companyData[o.Ticker].sharpeRatio,
          volatility: this.companyData[o.Ticker].volatility
        })
      })

      // order the slider data by the weight descending
      this.sliderData.sort((a, b) => b.allocation - a.allocation);
    }
    // delete the point if already exists
    if (this.customPointAdded) {
      if (this.chartInstance.series[3].data[this.chartInstance.series[3].data.length-1].name === 'Selected portfolio') {
        this.chartInstance.series[3].data[this.chartInstance.series[3].data.length-1].remove()
        this.customPointAdded = false;  
      }
    }
    // Update chart
    if (this.chartInstance && this.chartInstance.series && this.chartInstance.series[0] && this.chartInstance.series[0].data) {
      const dataPoint = this.chartInstance.series[0].data[index];
      if (dataPoint) {
        // emit the volatility so area chart can be updated
        this.sliderVolatility = dataPoint.x;

        this.chartInstance.series[3].addPoint({
          name: 'Selected portfolio',
          x: dataPoint.x,
          y: dataPoint.y,
          color: '#424ccd',
          custom: {
            sharpeRatio: dataPoint.y / dataPoint.x,
          }
        })
        this.customPointAdded = true;
        this.sliderPortfolioData = dataPoint
      }
    }
  }

  onSliderChange(event: any) {
    this.sliderVal = event;
    this.addPointToChart();
  }

  getFixedPointPosition(): string {
    return this.getPositionForIndex(this.fixedPointTangencyPoint);
  }

  getFixedMaxSharpePointPosition(): string {
    return this.getPositionForIndex(this.fixedMaxSharpePoint);
  }

  private getPositionForIndex(index: number | null): string {
    if (index === null || this.sliderMax <= 0) {
      return '0%';
    }
    
    // Check if slider element is available
    if (this.slider && this.slider.nativeElement) {
      let sliderWidth = this.slider.nativeElement.offsetWidth;
      sliderWidth = sliderWidth - 2; // remove padding
      if (sliderWidth > 0) {
        // Find index position in the slider data
        const indexPosition = this.series[0].data.findIndex(d => d.return_index === index);
        if (indexPosition >= 0) {
          const indexRatio = indexPosition / this.sliderMax;
          return indexRatio * sliderWidth + 'px';
        }
      }
    }
  }

  initSlider() {
    this.sliderLoaded = false;

    if (this.series && this.series.length && this.series[0].data.length) {
      // Keep all the positions, so efficient frontier slider can select all the data and allocations
      /* 
      // remove duplicated sharpeRatio values from the series data
      let uniqueData = this.series[0].data.filter((d, index, self) =>
        index === self.findIndex((t) => t.sharpeRatio === d.sharpeRatio)
      );
      this.sliderMax = uniqueData.length - 1;
      this.series[0].data = uniqueData; */
      this.sliderMax = this.series[0].data.length - 1;
    }
    if (this.efficientChartMaxSharpeData && this.series[0].data.length) {
      let maxSharpeIndex = 0;
      let maxSharpeValue = 0;
      // find the index of the tangency point
      this.series[0].data.map((d, index) => {
        if (d.sharpeRatio > maxSharpeValue) {
          maxSharpeValue = d.sharpeRatio;
          maxSharpeIndex = index;
        }
        if (d.x === this.efficientChartMaxSharpeData.x && d.y === this.efficientChartMaxSharpeData.y) {
          this.fixedPointTangencyPoint = index
        }
      })
      this.fixedMaxSharpePoint = maxSharpeIndex;

      if (!this.optim || this.optim == 'efficient_portfolio_max_sharpe_ratio') {
        this.sliderVal = this.fixedPointTangencyPoint;
        this.addPointToChart();
      } else if (this.optim == 'efficient_portfolio_given_mu') {
        const returnVal = this.optimizationData.optim_parameters.efficient_target_return;
        this.sliderVal = this.findReturnIndex(returnVal)
        this.addPointToChart();
      } 
    }
    this.sliderLoaded = true;
  }

  private findReturnIndex(returnVal) {
    let index = this.series[0].data.findIndex(d => d.y === returnVal);
    if (index > -1) {
      return index
    }
    // try to find the closest index if it is not in return list
    returnVal = returnVal - 0.01;
    index = this.series[0].data.findIndex(d => d.y === returnVal);
    if (index > -1) {
      return index
    }

    // still not found, try the other way
    returnVal = returnVal + 0.02;
    index = this.series[0].data.findIndex(d => d.y === returnVal);
    if (index > -1) {
      return index
    }
    console.log("Warning: Can not find efficient_target_return in chart data.")
    return 0;
  }

  initChart() {

    const setCurrentIndex = (idx, vol, ret) => {
      if (!this.customPointAdded) {
        this.currentIndex = idx;
        this.currentReturn = {
          vol: vol.toFixed(1),
          ret: ret.toFixed(1)
        }
        if(idx < 101){
          this.optimWeights = this.optimWeights.map(o => {
            o.weight = o.id_w[this.currentIndex].toFixed(1);
            return o;
          })
        }else{

        }

         this.dataSource.data = this.optimWeights.filter((o)=>o.weight > 0).sort((a, b) => {return parseFloat(b.weight) - parseFloat(a.weight)});

         this.series[0].data.map(d => {
            if (d.return_index === idx || idx == 101){
              d.weight = this.dataSource.data;
            }
            return d;
          });
         
      }
    }

    const handleLineClick = (point?) => {

      if (!this.customPointAdded) {
        if (point && point.series.name == 'Optim Efficient Frontier') {
          // Add custom point to the list
          this.chartInstance.series[2].addPoint({
            name: 'Clicked Point',
            x: point.x,
            y: point.y,
            color: '#40C6BA',
            custom: {
              sharpeRatio: point.y / point.x,
            }
          })
          this.customPointAdded = true;
        }
      } else {
        // Remove last point
        this.chartInstance.series[2].data[this.chartInstance.series[2].data.length-1].remove()
        this.customPointAdded = false;
      }
    };

    const findDataBehindOptim = (point?) => {
      
      this.series[0].data.map(d => {
       // if (d.x === point.x && d.y === point.y) {
          setCurrentIndex(101, d.x, d.y);
      //  }
      });
    };

    this.chartOptions = {
      chart: {
        height: '630px',
        events: {
          click: function() {
            handleLineClick();
          }
        }
      },
      credits: {
        enabled: false
      },
      title: {
        text: 'Efficient Frontier',
         style: {
            textTransform: 'none'
          }
      },
      xAxis: {
        title: {
          text: 'Expected Volatility',
          style: {
            textTransform: 'capitalize'
          }
        },
        labels: {
          format: '{value}%'
        },
        minPadding: 0.2,
        maxPadding: 0.2,
      },
      yAxis: {
        minorGridLineWidth: 0,
        title: {
          text: 'Expected Return',
          style: {
            textTransform: 'capitalize'
          }
        },
        labels: {
          format: '{value}%'
        }
      },
      legend: {
        enabled: false,
      },
      colors: ['#424CCD', '#0c0c68',  '#40C6BA'],
      
      series: this.series,
      tooltip: {
        enabled: true,
        formatter: function () {
          let data: any = (this as any).point;
          var res = ''
          if(data.options.weight)
            for(let i = 0; i< data.options.weight.length; i ++){
                res += "<br /><p style='color=#40c6ba'>" + data.options.weight[i].company_name + ": " + data.options.weight[i].weight + "%</p>";
            }         
          const sharpeRatio = data.options.custom?.sharpeRatio ?? data.options.sharpeRatio;
          return (data.company || data.name || 'Efficient Frontier') + '<br/> Expected Volatility: ' + data.x + '%<br/>Expected Return: ' + data.y + '%' + (sharpeRatio !== undefined ? '<br/>Sharpe Ratio: ' + +sharpeRatio.toFixed(2) : '')  + res;
        }
      },
      plotOptions: {
        scatter: {
          dataLabels: {
            enabled: true,
              formatter: function() {
                  let name: any = (this as any).point; 
                  var color = '#424CCD';
                  if(name.name == "Baseline Portfolio")
                    color = '#40c6ba';
                  if(name.name == "Max Sharpe" 
                  || name.name == 'Target Risk' 
                  || name.name == 'Max Return'
                  || name.name == 'Target Return'
                  || name.name == 'Minimum Risk'
                  ){
                    color = "#0c0c68";
                  } else if (name.name.includes("Benchmark")) {
                    color = '#FF8622';
                  }

                  return '<span style="color: ' + color + '; font-size: 12px;   ">' +  name.name + '</span>';   
              },
            allowOverlap: true,
            position: 'right',
          }
        },
        series: {
          point: {
            events: {
              mouseOver: function () {
                // Purple line hover, set weight index
                if (this.series.name == 'Optim Efficient Frontier') {
                  const point: any = this;
                  setCurrentIndex(point.return_index, point.x, point.y)
                }
                if (this.series.name == 'Optim') {
                  findDataBehindOptim(this);
                }
              },
              click: function(event) {
                handleLineClick(event.point)
              }
            }
          }
        }
      },
    };
    if (this.fullHeight) {
      this.chartOptions.chart.height = '88%';
    }
    setTimeout(() => {
      this.loaded = true;
      this.cdr.detectChanges();
    });
    setTimeout(() => {
      this.initSlider();
    }, 1000);
  }
  calculate(hasData = true) {
    // Disable risk analysis navigation for 10 seconds
    this.navigationStateService.disableRiskAnalysis();
    
    // Set timer to re-enable after 10 seconds
    setTimeout(() => {
      this.navigationStateService.enableRiskAnalysis();
    }, 10000);
    
    this.calculateClicked.emit({
      return: this.optimizationData.optim_parameters.efficient_target_return,
      risk: this.optimizationData.optim_parameters.efficient_target_risk,
      hasData: hasData
    })
  }

  onLockChange() {
    if (this.sliderLocked) {
      this.calculateWithSlider();
    }
  }

  calculateWithSlider() {    
    if (!this.sliderLocked) {
      return;
    }
    const sliderData = this.chartInstance.series[0].data[this.sliderVal].options;
    if (sliderData) {
      this.optimizationData.optim_parameters.efficient_target_return = sliderData.y;
      this.optimizationData.optim_parameters.action = "risk-analysis";
      this.service.changePfOptim('efficient_portfolio_given_mu');
      this.calculate(false);
    }
  }

  onPfChange(value: string){
    this.service.changePfOptim(value);
    this.calculate(false);
  }
  onStockToggleChange() {
    if (this.showStocks) {
      this.chartInstance.series[3].show();
    } else {
      this.chartInstance.series[3].hide();
    }
  }
  onBenchmarkToggleChange() {
    if (this.showBenchmark) {
      this.chartInstance.series[4].show();
    } else {
      this.chartInstance.series[4].hide();
    }
  }

  onEfWeightsToggleChange() {
    this.showEfWeights = !this.showEfWeights;
  }


  onOptimizeClick(){

    
  }
  onConstraintToggleChange() {
    if (this.showUnconstrainted) {
      this.chartInstance.series[2].show();
    } else {
      this.chartInstance.series[2].hide();
    }
  }

  formatLabel(value: number) {
    return '';
  }

  handleCardExport(key: string, element: HTMLElement | null, filename: string): void {
    if (!element) {
      this.service?.notificationService?.open('Card not found. Please try again.');
      return;
    }
    this.cardExporting[key] = true;
    const runExport = async () => {
      try {
        await this.exportService.exportElementAsImage(element, {
          filename
        });
      } finally {
        this.cardExporting[key] = false;
      }
    };
    setTimeout(runExport, 0);
  }
  exportResultsChart() {
    const element = document.querySelector('#efficient-frontier-chart-results') as HTMLElement;
    this.handleCardExport('results', element, `efficient-frontier-chart-results.jpeg`);
  }
  exportResultsTable() {
    const element = document.querySelector('#efficient-frontier-chart-results-table') as HTMLElement;
    this.handleCardExport('resultsTable', element, `efficient-frontier-chart-results-table.jpeg`);
  }
  exportTransitionMap() {
    const element = document.querySelector('#efficient-frontier-chart-transition-map') as HTMLElement;
    this.handleCardExport('transitionMap', element, `efficient-frontier-chart-transition-map.jpeg`);
  }
  exportSummaryWeightsDonut() {
    const element = document.querySelector('#efficient-frontier-chart-summary-weights-donut') as HTMLElement;
    this.handleCardExport('summaryWeightsDonut', element, `efficient-frontier-chart-summary-weights-donut.jpeg`);
  }
  exportSummaryReturnsDonut() {
    const element = document.querySelector('#efficient-frontier-chart-summary-returns-donut') as HTMLElement;
    this.handleCardExport('summaryReturnsDonut', element, `efficient-frontier-chart-summary-returns-donut.jpeg`);
  }
  

}
